import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ADD_DAYCARE_MEAL_ACCESSIBILITY } from '@/modules/API/meal/daycareSettingsHandler.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useMutation } from '@apollo/client'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import styles from '../../meal.module.scss'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { Daycare } from '@/types/Daycare.ts'
import { Section } from '@/types/Section.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import { MealType } from '@/types/Meal.ts'

interface AddMealAccessibilityDialogProps {
    open: boolean
    handleClose: () => void
    refetch: () => void
    daycare: Daycare
    isRPEOrLAEP: boolean
}

const AddMealAccessibilityDialog: FunctionComponent<AddMealAccessibilityDialogProps> = ({
    open,
    handleClose,
    refetch,
    daycare,
    isRPEOrLAEP
}: AddMealAccessibilityDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [sectionIds, setSectionIds] = useState<string[]>([])
    const [start, setStart] = useState<string>('')
    const [type, setType] = useState<string>('')

    const [addMealAccessibility] = useMutation(ADD_DAYCARE_MEAL_ACCESSIBILITY, {
        variables: {
            daycareId: daycare.id,
            sectionIds: sectionIds.map(Number),
            start,
            type
        },
        onCompleted: () => {
            refetch()
            handleClose()
            setSectionIds([])
            setStart('')
            setType('')
            showNotificationSuccess(t('mealAccessibilityAdded'))
        },
        onError: () => {
            handleClose()
            setSectionIds([])
            setStart('')
            setType('')
            showNotificationError(t('mealAccessibilityAddedError'))
        }
    })

    const mealTypeOptions: SelectOptionsDeprecated = [
        {
            options: Object.values(MealType).map(
                (mealType: MealType): SelectOptionDeprecated => ({
                    name: t('mealType' + mealType.charAt(0).toUpperCase() + mealType.slice(1)),
                    value: mealType
                })
            )
        }
    ]

    const sectionOptions: SelectOptionsDeprecated = [
        {
            options:
                daycare.sections?.map((section: Section): SelectOptionDeprecated => {
                    return {
                        name: section.name,
                        value: section.id.toString()
                    }
                }) || []
        }
    ]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addMealAccessibility')} />
            <DialogBody className={styles.DialogBody}>
                <SelectMultiple
                    label={isRPEOrLAEP ? t('mySectors') : t('mySections')}
                    selectOptions={sectionOptions}
                    selectValues={sectionIds}
                    setSelectValues={setSectionIds}
                    className={styles.UserInput}
                    fullWidth={true}
                />
                <TimePicker
                    label={t('hour')}
                    value={start}
                    setValue={setStart}
                    className={styles.UserInput}
                    minuteToDisable={5}
                />
                <SelectUnique
                    label={t('mealType')}
                    selectOptions={mealTypeOptions}
                    selectValue={type}
                    setSelectValue={setType}
                    className={styles.UserInput}
                    fullWidth={true}
                />
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type='add'
                        disabled={sectionIds.length === 0 || start === '' || type === ''}
                        onClick={() => addMealAccessibility()}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddMealAccessibilityDialog
